import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageDetailLayout } from '@/shared/layout';
import { useLogDetail, useLogs } from './hooks/useLogs';
import { Button, Badge, Card, FeaturePlaceholder, Alert, KPICard, Table, Avatar } from '@/components/ui';
import { LogFormModal } from './components/LogFormModal';
import { AuthButton } from '@/core/permissions/AuthButton';
import { useAuth } from '@/core/auth/AuthContext';
import { toast } from 'sonner';

const statusColors: Record<string, string> = {
  draft: 'bg-slate-100 text-slate-500',
  submitted: 'bg-blue-100 text-blue-600',
  approved: 'bg-emerald-100 text-emerald-600',
  rejected: 'bg-red-100 text-red-600',
};

const statusLabels: Record<string, string> = {
  draft: 'Bản nháp',
  submitted: 'Chờ duyệt',
  approved: 'Đã duyệt',
  rejected: 'Từ chối',
};

export default function LogDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';

  const { log, media, isLoading } = useLogDetail(id);
  const { updateStatus, isUpdatingStatus } = useLogs(companyId);

  const [activeTab, setActiveTab] = useState('overview');
  const [showEdit, setShowEdit] = useState(false);

  if (isLoading) return <div className="p-12 text-center text-slate-500 text-sm">Đang tải chi tiết nhật ký...</div>;
  if (!log) return <div className="p-12 text-center text-red-500 font-bold">Không tìm thấy dữ liệu nhật ký</div>;

  const handleStatusChange = async (newStatus: 'approved' | 'rejected' | 'submitted') => {
    try {
      await updateStatus({ id: id!, status: newStatus });
      toast.success(`Đã cập nhật trạng thái sang ${statusLabels[newStatus]}`);
    } catch (error: any) {
      toast.error('Lỗi: ' + error.message);
    }
  };

  const tabs = [
    { value: 'overview', label: 'Tổng quan' },
    { value: 'media', label: 'Hình ảnh hiện trường' },
    { value: 'checklist', label: 'Checklist QA' },
    { value: 'history', label: 'Lịch sử' },
  ];

  return (
    <PageDetailLayout
      title={`${log.task_name}`}
      description={
        <div className="flex items-center gap-3 mt-1">
          <Badge className={`${statusColors[log.status_code] || 'bg-slate-100 text-slate-600'} border-transparent text-[10px] font-bold `}>
            {statusLabels[log.status_code] || log.status_code}
          </Badge>
          <span className="text-sm text-slate-400 font-mono">#{log.id.slice(-6).toUpperCase()}</span>
          <span className="text-slate-300">|</span>
          <span className="text-sm text-slate-500 flex items-center gap-1">
            <i className="ri-user-follow-line" />
            {log.worker?.full_name}
          </span>
          <span className="text-slate-300">|</span>
          <span className="text-sm text-slate-500 flex items-center gap-1">
            <i className="ri-calendar-line" />
            {new Date(log.log_date).toLocaleDateString('vi-VN')}
          </span>
        </div>
      }
      breadcrumbs={[
        { label: 'Nhật ký bảo dưỡng', path: '/maintenance/logs' },
        { label: log.task_name }
      ]}
      headerActions={
        <div className="flex gap-2">
          {log.status_code === 'submitted' && (
            <>
              <AuthButton module="maintenance" action="approve" variant="success" icon="ri-check-line" onClick={() => handleStatusChange('approved')} isLoading={isUpdatingStatus}>
                Duyệt nhật ký
              </AuthButton>
              <AuthButton module="maintenance" action="reject" variant="danger" icon="ri-close-line" onClick={() => handleStatusChange('rejected')} isLoading={isUpdatingStatus}>
                Từ chối
              </AuthButton>
            </>
          )}
          {log.status_code === 'draft' && (
            <AuthButton module="maintenance" action="update" variant="primary" icon="ri-send-plane-line" onClick={() => handleStatusChange('submitted')} isLoading={isUpdatingStatus}>
              Gửi duyệt
            </AuthButton>
          )}
          <AuthButton module="maintenance" action="update" variant="outline" icon="ri-edit-line" onClick={() => setShowEdit(true)}>
            Chỉnh sửa
          </AuthButton>
        </div>
      }
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {activeTab === 'overview' && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <KPICard title="Khối lượng" value={log.quantity_actual} icon="ri-bubble-chart-line" variant="info" suffix={` ${log.unit}`} />
                <KPICard title="Thời lượng" value={log.duration_minutes || 0} icon="ri-time-line" variant="warning" suffix=" phút" />
                <KPICard title="Hình ảnh" value={media.length} icon="ri-image-line" variant="neutral" />
              </div>

              <Card className="p-6">
                <h3 className="font-bold text-slate-900 mb-6 flex items-center gap-2">
                  <i className="ri-information-line text-os-primary" />
                  Thông tin thực hiện
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-12">
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Dự án</span>
                    <span className="text-sm font-bold text-slate-700">{log.project?.name || '---'}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Khu vực (Zone)</span>
                    <span className="text-sm font-bold text-os-primary">{log.zone?.zone_name || '---'}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Lệnh công việc</span>
                    <span className="text-sm font-bold text-slate-700 font-mono">{log.work_order?.order_number || 'Báo cáo độc lập'}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Loại công việc</span>
                    <span className="text-sm font-bold text-slate-700 capitalize">{log.task_type}</span>
                  </div>
                </div>
                <div className="mt-6">
                  <label className="block text-[10px] font-bold text-slate-400  mb-2">Mô tả chi tiết</label>
                  <p className="text-sm text-slate-600 bg-slate-50 p-4 rounded-2xl border border-slate-100 italic leading-relaxed">
                    {log.description || 'Không có mô tả chi tiết cho nhật ký này.'}
                  </p>
                </div>
              </Card>

              <Card className="p-6">
                <h3 className="font-bold text-slate-900 mb-6 flex items-center gap-2">
                  <i className="ri-user-follow-line text-os-primary" />
                  Nhân sự thực hiện
                </h3>
                <div className="flex items-center gap-4 bg-slate-50 p-4 rounded-2xl border border-slate-100">
                  <Avatar src={log.worker?.avatar_url} name={log.worker?.full_name} size="lg" />
                  <div>
                    <p className="text-sm font-bold text-slate-900">{log.worker?.full_name}</p>
                    <p className="text-xs text-slate-500">{log.team?.name || 'Vận hành tự do'}</p>
                  </div>
                </div>
              </Card>
            </>
          )}

          {activeTab === 'media' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {media.map(m => (
                <Card key={m.id} className="overflow-hidden group relative">
                  <img src={m.file_url} alt={m.type} className="w-full h-64 object-cover transition-transform group-hover:scale-105" />
                  <div className="absolute top-3 left-3">
                    <Badge className="bg-slate-900/80 text-white border-transparent  text-[9px] font-bold px-2 py-1">
                      {m.type}
                    </Badge>
                  </div>
                  <div className="p-3 bg-white/90 backdrop-blur-sm absolute bottom-0 left-0 right-0 border-t border-slate-100">
                    <p className="text-[10px] text-slate-500 flex items-center gap-1 font-bold  tracking-wider">
                      <i className="ri-time-line" />
                      {new Date(m.captured_at).toLocaleString('vi-VN')}
                    </p>
                  </div>
                </Card>
              ))}
              {media.length === 0 && (
                <div className="col-span-2 py-12 text-center bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                  <i className="ri-image-add-line text-4xl text-slate-300 mb-2 block" />
                  <p className="text-sm text-slate-400">Chưa có hình ảnh hiện trường được tải lên.</p>
                </div>
              )}
            </div>
          )}

          {(activeTab === 'checklist' || activeTab === 'history') && (
            <FeaturePlaceholder
              title={tabs.find(t => t.value === activeTab)?.label || 'Tính năng'}
              description="Dữ liệu đang được đồng bộ hóa từ hệ thống giám sát hiện trường."
            />
          )}
        </div>

        <div className="space-y-6">
          <Card className="p-6 bg-slate-900 text-white">
            <h4 className="font-bold mb-4 flex items-center gap-2">
              <i className="ri-time-line text-os-primary" />
              Thời gian thực hiện
            </h4>
            <div className="space-y-4">
              <div className="flex justify-between items-center text-xs">
                <span className="text-slate-400  font-bold tracking-wider">Trạng thái</span>
                <Badge className={`${statusColors[log.status_code]} border-transparent text-[10px] font-bold `}>
                  {statusLabels[log.status_code]}
                </Badge>
              </div>
              <div className="flex justify-between items-center text-xs">
                <span className="text-slate-400">Bắt đầu</span>
                <span className="font-bold">{log.start_time || '--:--'}</span>
              </div>
              <div className="flex justify-between items-center text-xs">
                <span className="text-slate-400">Kết thúc</span>
                <span className="font-bold">{log.end_time || '--:--'}</span>
              </div>
              <div className="pt-4 border-t border-slate-800">
                <Alert variant="info" className="bg-slate-800 border-slate-700 text-slate-300 text-[11px] leading-relaxed">
                  <i className="ri-shield-user-line text-os-primary mr-1" />
                  Nhật ký này được xác nhận bởi GPS và mã định danh thiết bị của nhân viên.
                </Alert>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <h4 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
              <i className="ri-shield-check-line text-slate-400" />
              Phê duyệt nhật ký
            </h4>
            <div className="space-y-3">
              {log.status_code === 'submitted' ? (
                <>
                  <Button variant="primary" className="w-full" onClick={() => handleStatusChange('approved')}>Chấp nhận nhật ký</Button>
                  <Button variant="outline" className="w-full text-red-500 hover:bg-red-50" onClick={() => handleStatusChange('rejected')}>Từ chối & Yêu cầu sửa</Button>
                </>
              ) : (
                <Alert variant="neutral" icon="ri-checkbox-circle-line">
                  {log.status_code === 'approved' ? 'Nhật ký đã được phê duyệt và ghi nhận khối lượng.' : 'Nhật ký chưa ở trạng thái chờ duyệt.'}
                </Alert>
              )}
            </div>
          </Card>
        </div>
      </div>

      <LogFormModal
        isOpen={showEdit}
        item={log}
        companyId={companyId}
        onClose={() => setShowEdit(false)}
      />
    </PageDetailLayout>
  );
}
